import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { ControlContainer, NgForm } from '@angular/forms';
import { ConstantMan } from '../../../../../../../core/services/constantMan';
import { FullCalendarComponent, CalendarOptions } from '@fullcalendar/angular';
import { toMoment } from '@fullcalendar/moment';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';
import { CharterDAO } from '../../../../../../../core/services/dao/charterDAO';
import * as moment from 'moment';
import { CalendarEvent } from '../../../../../../../core/models/interface/calendar-event';
import { VesselDAO } from '../../../../../../../core/services/dao/vesselDAO';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogsService } from '../../../../../../../core/services/dialog.service';
import { NumberFormatterService } from '../../../../../../../core/services/number.formatter.service';
import { isPlatformBrowser } from '@angular/common';
import { UserDAO } from '../../../../../../../core/services/dao/userDAO';
import { SchedulePricingComponent } from './schedule-pricing/schedule-pricing.component';
import { CalendarPreferencesDAO } from '../../../../../../../core/services/dao/calendarPreferencesDAO';
import { Subscription } from 'rxjs';
import { unsubscribeFromAll } from '../../../../../../../core/pure-functions/rx-helper';
import { CalendarMobileHelpDialogComponent } from './calendar-mobile-help-dialog/calendar-mobile-help-dialog.component';
import { ContainsSubscriptions } from '../../../../../../../core/services/interfaces/contains-subscriptions';
import { MatSnackBar } from '@angular/material/snack-bar';
import { getUtcCharterDate } from '../../../../../../../core/models/charter';
import { AuthService } from '../../../../../../../core/services/auth.service';
import { CalendarConnectDialogComponent } from './calendar-connect-dialog/calendar-connect-dialog.component';
import { ChickenTestDialogService } from '../../../../../../shared/dialogs/chicken-test-dialog/chicken-test-dialog.service';
import { ForeignKeyType } from 'anchor-shared/lib/model/enum/foreign-key-type';

@Component({
  selector: 'app-charter-schedule-form',
  templateUrl: './charter-schedule.component.html',
  styleUrls: ['./charter-schedule.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class CharterScheduleFormComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  @ViewChild(FullCalendarComponent)
  ucCalendar: FullCalendarComponent;
  @Input() model: Vessel;
  subs: Subscription[] = [];
  calendarOptions: CalendarOptions;
  events: any[] = [];
  public readonly MOBILE_CALENDAR_HEIGHT = 400;
  isBrowser: boolean;
  collapseCalendarHowTo: boolean;
  isMobile: boolean;
  isAdmin: boolean;
  googleIcon: string =
    'https://anchor-public-media.s3.us-east-2.amazonaws.com/prod/Google_Calendar_icon.svg.png';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private vesselDao: VesselDAO,
    private dialogService: DialogsService,
    private charterDao: CharterDAO,
    private calendarPreferencesDAO: CalendarPreferencesDAO,
    private numFormatterService: NumberFormatterService,
    private userDao: UserDAO,
    @Inject(PLATFORM_ID) private platformId: any,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private chickenTestDialog: ChickenTestDialogService,
  ) {
    this.collapseCalendarHowTo = false;
    this.isBrowser = isPlatformBrowser(this.platformId);

    this.isAdmin = this.userDao.currentUserIsAdmin();
  }

  ngOnInit() {
    this.isMobile = window && window.screen && window.screen.width < 960;
    this.getCurrentCharters();
    this.openCalSyncFromQueryParams();
  }

  select(eventDetail: any) {
    let calendarApi = this.ucCalendar.getApi();
    if (
      toMoment(eventDetail.start, calendarApi).isBefore(
        moment().subtract(1, 'day'),
      )
    ) {
      this.snackBar.open(
        "Please select a new date; a calendar preference can't be added to the past.",
        null,
        {
          duration: 5000,
          verticalPosition: 'top',
        },
      );
      calendarApi.unselect();
      return;
    }
    const checkIfDateExistsSub = this.calendarPreferencesDAO
      .checkIfDateExists(this.model.id, {
        startDate: toMoment(eventDetail.start, calendarApi).format(),
        endDate: toMoment(eventDetail.end, calendarApi).format(),
      })
      .subscribe((event) => {
        if (event.conflict && !this.isAdmin) {
          this.snackBar.open(
            'Please select a new date; a calendar preference already exists.',
            null,
            {
              duration: 5000,
              verticalPosition: 'top',
            },
          );
          this.ucCalendar.getApi().unselect();
          return;
        } else {
          eventDetail.start = toMoment(eventDetail.start, calendarApi);
          eventDetail.end = toMoment(eventDetail.end, calendarApi);
          let config: any = {
            data: {
              eventDetail: eventDetail,
              vessel: this.model,
            },
          };

          config.maxHeight = '100vh';
          config.maxWidth = '100vw';

          if (this.isMobile) {
            config.width = '100%';
            config.height = '100%';
          }
          const scheduleDialogSub = this.dialog
            .open(SchedulePricingComponent, config)
            .afterClosed()
            .subscribe((data) => {
              if (!data || data.cancel) {
                return;
              } else if (data.preferenceData) {
                const preferenceSub = this.calendarPreferencesDAO
                  .createNewPreference(data.preferenceData)
                  .subscribe((newCalPref) => {
                    this.model.calendarPreferences.push(newCalPref);
                    this.renderNewSelection(newCalPref);
                  });
                this.subs.push(preferenceSub);
              }
            });
          this.subs.push(scheduleDialogSub);
        }
      });
    this.subs.push(checkIfDateExistsSub);
  }

  private deleteOrUpdate(eventDetail: any) {
    if (
      eventDetail.event._def.extendedProps.eventType ===
        ConstantMan.APP_CALENDAR.UNAVAILABLE ||
      eventDetail.event._def.extendedProps.eventType ===
        ConstantMan.APP_CALENDAR.DAILY_CONFIGURATION
    ) {
      let config: any = {
        data: {
          eventDetail: eventDetail.event._def.extendedProps,
          vessel: this.model,
        },
      };
      if (this.isMobile) {
        config.width = '100vh';
        config.height = '100vh';
        config.maxWidth = '100vh';
        config.maxHeight = '100vh';
      }
      const scheduleDialogSub = this.dialog
        .open(SchedulePricingComponent, config)
        .afterClosed()
        .subscribe((data) => {
          if (!data || data.cancel) {
            return;
          } else if (data && data.preferenceData.id && !data.deleted) {
            let id = data.preferenceData.id;
            delete data.preferenceData.id;
            const UpdatePreferenceSub = this.calendarPreferencesDAO
              .updateFromPartial(
                id,
                data.preferenceData,
                data.preferenceData.vesselId,
              )
              .subscribe((updatedCalPref) => {
                this.updateSelection(updatedCalPref);
              });
            this.subs.push(UpdatePreferenceSub);
          } else if (data.deleted) {
            this.deleteSelection(data.preferenceData);
          }
        });
      this.subs.push(scheduleDialogSub);
    } else {
      this.dialogService.HelpDialog(
        eventDetail.event._def.extendedProps.dialogInfo,
      );
    }
  }

  private getCurrentCharters(): void {
    const loadCharterSub = this.charterDao
      .loadChartersShallowByVesselId(this.model.id)
      .subscribe((chartersForVessel) => {
        const calEvents = [];
        let calEvent = null;
        chartersForVessel.forEach((charter) => {
          const eventColor = charter.paymentReceived ? 'green' : 'orange';
          calEvent = {
            eventType: ConstantMan.APP_CALENDAR.ALLOCATED,
            title: `ID: ${charter.id} - ${charter.customerFirstName} ${charter.customerLastName}`,
            start: getUtcCharterDate(charter).startOf('day').format(),
            end: getUtcCharterDate(charter)
              .add(charter.duration, 'hours')
              .endOf('hours')
              .format(),
            color: eventColor,
            charterId: charter.id,
            dialogInfo: `<h4>
              <b>Charter Date:</b> ${getUtcCharterDate(charter).format(
                'MM-DD-YY',
              )} <br>
              <b>Deposit Status:</b> ${charter.depositStatus} <br>
              <b>Charter Duration:</b> ${charter.duration} hours <br>
              <b>Charter Start:</b> ${charter.startTime} <br>
              <b>Charter End:</b> ${charter.charterEndTime} <br>
              <b>Number of Passengers:</b> ${charter.numOfPassengers} <br>
              ${
                charter.paymentReceived
                  ? '<b>Customer:</b> ' +
                    charter.customerFirstName +
                    ' ' +
                    charter.customerLastName +
                    '<br>'
                  : ''
              }
              ${
                charter.paymentReceived
                  ? '<b>Customer Phone:</b> ' +
                    charter.customerPhoneNumber +
                    '<br>'
                  : ''
              }
              <b>Captain:</b> ${
                charter.captainFirstName
                  ? charter.captainFirstName
                  : 'Captain is '
              }
              ${charter.captainLastName ? charter.captainLastName : 'TBD'} <br>
              <b>Captain Phone:</b> ${
                charter.captainPhoneNumber ? charter.captainPhoneNumber : ''
              } <br>
              <a href="${
                window.location.origin + '/manage/manage-charter/' + charter.id
              }" target="_blank" style="color: #337AB7">${
              this.isAdmin ? 'Manage Charter' : ''
            }</a> <br>
              </h4>`,
            uniqueIdentifier: `${charter.id}`,
          };
          calEvents.push(calEvent);
          this.events.push(calEvent);
        });
        console.log('calEvents', this.events);
        this.prepareEvents();
        this.createCalendarOptions();
      });
    this.subs.push(loadCharterSub);
  }

  private createCalendarOptions(): void {
    this.calendarOptions = {
      timeZone: 'UTC',
      themeSystem: 'standard',
      displayEventTime: false,
      selectable: true,
      selectOverlap: true,
      longPressDelay: 200,
      contentHeight: this.getCalendarHeight(),
      events: this.events,
      headerToolbar: {
        start: 'prev,next',
        center: 'title',
        end: 'today',
      },
      buttonText: {
        prev: 'Prev',
        next: 'Next',
      },
      buttonIcons: {
        prev: 'chevron-left',
        next: 'chevron-right',
      },
      initialView: 'dayGridMonth',
      eventClick: this.deleteOrUpdate.bind(this),
      select: this.select.bind(this),
    };
  }

  public getCalendarHeight(): number {
    if (screen.width < 577) {
      return this.MOBILE_CALENDAR_HEIGHT;
    }
    return null;
  }

  public async renderNewSelection(eventDetail: any) {
    let eventType = await setEventType(eventDetail);
    let calendarApi = this.ucCalendar.getApi();
    const event = {
      id: eventDetail.id,
      calendarPreferencesId: eventDetail.id,
      calendarPreferences: eventDetail,
      eventType: eventType.eventType,
      title: eventType.title,
      start: eventDetail.fromDate,
      end: eventDetail.toDate,
      color: eventType.color,
      textColor: eventType.textColor,
      allDay: eventType.allDay,
    };
    this.events.push(event);
    calendarApi.addEvent(event);
  }

  deleteSelection(eventToDelete) {
    let calendarApi = this.ucCalendar.getApi();
    calendarApi.getEventById(eventToDelete.id).remove();
    this.events = this.events.filter((event) => event.id !== eventToDelete.id);
    this.model.calendarPreferences = this.model.calendarPreferences.filter(
      (calendarPreferences) => calendarPreferences.id !== eventToDelete.id,
    );
  }

  updateSelection(eventToUpdate) {
    this.events = this.events.map((event) => {
      if (
        event.eventType === ConstantMan.APP_CALENDAR.UNAVAILABLE ||
        event.eventType === ConstantMan.APP_CALENDAR.DAILY_CONFIGURATION
      ) {
        if (
          event.calendarPreferencesId &&
          event.calendarPreferencesId === eventToUpdate.id
        ) {
          let eventType = setEventType(eventToUpdate);
          let newEventToUpdate = {
            ...event,
            eventType: eventType.eventType,
            title: eventType.title,
            color: eventType.color,
            textColor: eventType.textColor,
            allDay: eventType.allDay,
            calendarPreferencesId: eventToUpdate.id,
            calendarPreferences: eventToUpdate,
          };
          let calendarApi = this.ucCalendar.getApi();
          calendarApi.getEventById(eventToUpdate.id).remove();
          calendarApi.addEvent(newEventToUpdate);
          return newEventToUpdate;
        } else {
          return event;
        }
      } else {
        return event;
      }
    });
    const vesselCalendarPreferencesUpdateSub = this.vesselDao
      .getByVesselId(this.model.id)
      .subscribe((vessel) => {
        this.model.calendarPreferences = vessel.calendarPreferences;
      });
    this.subs.push(vesselCalendarPreferencesUpdateSub);
  }

  private prepareEvents(): any {
    const vesselEvents = [];
    const vesselUnavailabilities = this.model.calendarPreferences;
    if (vesselUnavailabilities) {
      vesselUnavailabilities.forEach((value) => {
        let eventType = setEventType(value);
        const calEvent = {
          id: value.id,
          calendarPreferencesId: value.id,
          calendarPreferences: value,
          eventType: eventType.eventType,
          title: eventType.title,
          start: value.fromDate,
          end: value.toDate,
          color: eventType.color,
          textColor: eventType.textColor,
          allDay: eventType.allDay,
        };
        if (calEvent.eventType === ConstantMan.APP_CALENDAR.GOOGLE_EVENT) {
          calEvent[
            'dialogInfo'
          ] = `<h3>This event makes your boat unavailable on Anchor.</h3>
            <br/>
            <p>Start time: ${value.unavailableTimes[0].startTime}</p>
            <p>End time: ${value.unavailableTimes[0].endTime}</p>
            <br/>
            <h4> To remove this event remove it from your Google Calendar.</h4>
            <p>Please allow up to three hours for your calendar to sync with Anchor.</p>`;
        }
        this.events.push(calEvent);
        vesselEvents.push(calEvent);
      });
    }

    return vesselEvents;
  }

  deleteCalendar() {
    const chickenSub = this.chickenTestDialog
      .open(
        null,
        'Yes',
        'No keep',
        35,
        null,
        'Delete synced calendar?',
        false,
        null,
        true,
      )
      .subscribe((remove) => {
        if (remove) {
          const sub = this.calendarPreferencesDAO
            .deleteAllGoogleCalsByVesselId(this.model.id)
            .subscribe((events) => {
              const vesselSub = this.vesselDao
                .updateFromPartial(this.model.id, {
                  calendarId: null,
                  calendarSyncToken: null,
                  calendarSummary: null,
                })
                .subscribe(() => {
                  this.model.calendarId = null;
                  this.model.calendarSyncToken = null;
                  this.model.calendarSummary = null;
                  this.model.calendarPreferences = events;
                  this.events = [];
                  this.getCurrentCharters();
                });
              this.subs.push(vesselSub);
            });
          this.subs.push(sub);
        }
      });
    this.subs.push(chickenSub);
  }

  openCalendarSync() {
    let config: any = {
      width: '100%',
      height: '100%',
      maxWidth: '100vh',
      maxHeight: '100vh',
      data: {
        vesselId: this.model.id,
        boatOwner: this.model.owner,
        vessel: this.model,
      },
    };

    if (!this.isMobile) {
      config.width = '60%';
      config.height = null;
    }

    const helpDialogSub = this.dialog
      .open(CalendarConnectDialogComponent, config)
      .afterClosed()
      .subscribe((data) => {
        if (data) {
          this.model.calendarId = data.calendarId;
          this.model.calendarSummary = data.calendarSummary;
          this.authService
            .syncBoatOwnerGoogleCalendar(this.model.id)
            .subscribe((events) => {
              this.model.calendarPreferences = events;
              this.events = [];
              this.getCurrentCharters();
            });
        }
      });
    this.subs.push(helpDialogSub);
  }

  openCalSyncFromQueryParams() {
    setTimeout(() => {
      if (this.route.snapshot.queryParams?.openCalSync) {
        this.openCalendarSync();
        this.router
          .navigate([], {
            queryParams: { openCalSync: null },
            queryParamsHandling: 'merge',
          })
          .catch((mergeQueryParamError) => {
            console.error(mergeQueryParamError);
          });
      }
    }, 10);
  }

  openHelp() {
    let config: any = {
      width: '100%',
      height: '100%',
      maxWidth: '100vh',
      maxHeight: '100vh',
    };

    if (!this.isMobile) {
      config.width = '80%';
      config.height = '60%';
    }

    const helpDialogSub = this.dialog
      .open(CalendarMobileHelpDialogComponent, config)
      .afterClosed()
      .subscribe((data) => {});
    this.subs.push(helpDialogSub);
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }
}

export function setEventType(eventDetail): SetEventType {
  if (
    eventDetail?.foreignKey &&
    eventDetail?.foreignKeyType === ForeignKeyType.GOOGLE_CALENDAR
  ) {
    return {
      eventType: ConstantMan.APP_CALENDAR.GOOGLE_EVENT,
      title: 'Google Event',
      color: '#ea4335',
      textColor: '#FFFFFF',
      allDay: true,
      dialogInfo: `This event was imported from your Google calendar.`,
    };
  } else if (
    eventDetail.available &&
    eventDetail.priceAdjustmentPercentage === 0
  ) {
    return {
      eventType: ConstantMan.APP_CALENDAR.DAILY_CONFIGURATION,
      title: 'Custom',
      color: '#3F509D',
      textColor: '#FFFFFF',
      allDay: true,
      dialogInfo: `The vessel has day specific charter durations and customizations`,
    };
  } else if (
    eventDetail.available &&
    eventDetail.priceAdjustmentPercentage > 0 &&
    eventDetail.charterDurationOfferings.fullDayDuration &&
    eventDetail.charterDurationOfferings.halfDayDuration
  ) {
    return {
      eventType: ConstantMan.APP_CALENDAR.DAILY_CONFIGURATION,
      title: `^ ${eventDetail.priceAdjustmentPercentage}% Custom`,
      color: '#A5DBDA',
      textColor: '#000000',
      allDay: true,
      dialogInfo: dialogInfoBuilder(
        'premium',
        eventDetail.priceAdjustmentPercentage,
      ),
    };
  } else if (
    eventDetail.available &&
    eventDetail.priceAdjustmentPercentage < 0 &&
    eventDetail.charterDurationOfferings.fullDayDuration &&
    eventDetail.charterDurationOfferings.halfDayDuration
  ) {
    return {
      eventType: ConstantMan.APP_CALENDAR.DAILY_CONFIGURATION,
      title: `v ${eventDetail.priceAdjustmentPercentage}% Custom`,
      color: '#F26868',
      textColor: '#FFFFFF',
      allDay: true,
      dialogInfo: dialogInfoBuilder(
        'discount',
        eventDetail.priceAdjustmentPercentage,
      ),
    };
  } else if (
    eventDetail.available &&
    eventDetail.priceAdjustmentPercentage > 0
  ) {
    return {
      eventType: ConstantMan.APP_CALENDAR.DAILY_CONFIGURATION,
      title: `^ ${eventDetail.priceAdjustmentPercentage}% Custom`,
      color: '#A5DBDA',
      textColor: '#000000',
      allDay: true,
      dialogInfo: dialogInfoBuilder(
        'premium',
        eventDetail.priceAdjustmentPercentage,
      ),
    };
  } else if (
    eventDetail.available &&
    eventDetail.priceAdjustmentPercentage < 0
  ) {
    return {
      eventType: ConstantMan.APP_CALENDAR.DAILY_CONFIGURATION,
      title: `v ${eventDetail.priceAdjustmentPercentage}% Custom`,
      color: '#F26868',
      textColor: '#FFFFFF',
      allDay: true,
      dialogInfo: dialogInfoBuilder(
        'discount',
        eventDetail.priceAdjustmentPercentage,
      ),
    };
  } else {
    return {
      eventType: ConstantMan.APP_CALENDAR.UNAVAILABLE,
      title: 'Unavailable',
      color: 'black',
      textColor: '#FFFFFF',
      allDay: true,
      dialogInfo: `Vessel is unavailable for this date.`,
    };
  }
}

function dialogInfoBuilder(
  verbiageType: 'discount' | 'premium',
  percentage: number | string,
): string {
  return `The vessel has a ${verbiageType} of ${percentage}% and has day specific charter durations`;
}

export interface SetEventType {
  eventType: string;
  title: string;
  color: string;
  textColor: string;
  allDay: boolean;
  dialogInfo: string;
}
